import { React, useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Button, Text, MTable, Tooltip, Copy, DeviceStatus, Popover, XCircle, Search } from "@cisco/spaces-dash-lib-styleguide";
import { NodeContext } from "../../contexts/nodeContext";
import CameraEmpty from "../../assets/images/location-accesspoints/camera_empty_lh2.svg";
import * as BusinessHierarchyService from "../../service/business-hierarchy-service";
import { I18nContext } from "../../contexts/i18nContext";
import CustomSearch from "../custom-search/custom-search";
import useLoader from "../../hooks/useLoader";
import "../../common/common_style.scss";
import "../../common/global_style.scss";
import "./security-appliance.scss";

function SecurityAppliance(props) {
  const i18n = useContext(I18nContext);
  const navigate = useNavigate();
  const { node, spCount, locationRowMap } = useContext(NodeContext);
  const [locationNode] = useState({ ...node });
  const [spListState, setSpListState] = useState([]);
  const [spListPagedState, setSpListPagedState] = useState([]);
  const [searchText, setSearchText] = useState("");
  const { setLoading } = useLoader();
  const [textColor, setTextColor] = useState("#f7f7f7");
  const [bgColor, setBgColor] = useState({ background: "#656c75" });
  const rsearchText = useRef("");
  const rtotalNoOfItems = useRef(spCount);
  const [showTable, setShowTable] = useState(false);
  const [copyIcon, setCopyIcon] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const hoveredItemId = useRef(null);

  let spList = [];
  let spListFILTERED = [];
  let spListPaged = [];
  const appUserSearchSkeleton = {
    displayName: "string",
    macAddress: "string"
  };

  const getLocationPath = (apId) => {
    try {
      let locNameArr = [];
      const parentLoc = locationRowMap[apId];
      if (!parentLoc) {
        return "";
      }
      const locPathIds = parentLoc.locationPath;
      if (locPathIds && locPathIds.length) {
        locNameArr = locPathIds
          .map((id) => {
            const loc = locationRowMap[id];
            return loc ? loc.name : null;
          })
          .filter((name) => name !== null);
      }
      const locNameStr = locNameArr.length ? locNameArr.join(" > ") : "";
      return locNameStr;
    } catch (e) {
      return "";
    }
  };

  const getLocationPathTruncated = (apId) => {
    const locPath = getLocationPath(apId);
    if (locPath.indexOf(" > ") - 1) {
      const count = locPath.split(" > ").length - 1;
      if (count > 1) {
        const arr = locPath.split(" > ");
        const newLocPathArr = [];
        newLocPathArr.push(arr[0]);
        newLocPathArr.push(arr[arr.length - 1]);
        const newLocPath = newLocPathArr.join(" > ... > ");
        return newLocPath;
      }
      return locPath;
    }
    return locPath;
  };

  const copyLocationPath = (locationPath) => {
    navigator.clipboard.writeText(locationPath);
    setIsCopied(true);
    setBgColor({
      ...bgColor,
      background: "#FFFF"
    });
    setTextColor("#545454");
  };
  const getCellValueWithToolTip = ({ row }) => {
    return (
      <div
        onMouseEnter={(val) => {
          console.log(val.currentTarget.attributes.getNamedItem("id").value);
          hoveredItemId.current = val.currentTarget.attributes.getNamedItem("id").value;
          setCopyIcon(true);
        }}
        onMouseLeave={() => {
          setIsCopied(false);
          setCopyIcon(false);
          setBgColor({
            ...bgColor,
            background: "#656c75"
          });
          setTextColor("#f7f7f7");
        }}
        id={row.id}
      >
        <Flex direction="horizontal" gap={2} align="center" justify="flex-start">
          <Tooltip title={row.original.locationPath} placement="bottom">
            <span className="hover-target">{row.original.locationPathTruncated}</span>
          </Tooltip>
          <Popover
            style={bgColor}
            placement="top"
            kind={isCopied ? "default" : "map"}
            title={
              <Flex direction="horizontal" gap={8} align="center" justify="space-between">
                {isCopied && (
                  <>
                    <DeviceStatus status="good" />
                    <Text color={textColor} size="p4" weight="regular">
                      {i18n.t("hierarchy-base.copied-to-clipboard")}
                    </Text>
                  </>
                )}
                {!isCopied && (
                  <Text color={textColor} size="p4" weight="regular">
                    {i18n.t("hierarchy-base.copy-to-clipboard")}
                  </Text>
                )}
              </Flex>
            }
            visible
          >
            <span className="hover-target">
              <Button
                icon={
                  <Copy
                    size={15}
                    onClick={() => {
                      copyLocationPath(row.original.locationPath);
                    }}
                  />
                }
                kind="tertiary"
                size="sm"
              />
            </span>
          </Popover>
        </Flex>
      </div>
    );
  };

  const searchSpList = (data) => {
    let tempApList = data;
    if (tempApList && tempApList.length > 0) {
      setShowTable(true);
    }
    tempApList = tempApList.map((item) => ({
      ...item,
      displayName: item.name,
      locationId: item.locationDetails ? item.locationDetails.id : "",
      locationName: item.locationDetails ? item.locationDetails.name : ""
    }));
    spList = tempApList;
    // setSpListState(tempApList);
    spListFILTERED = spList.map((sp) => ({
      ...sp,
      locationPath: getLocationPath(sp?.locationDetails?.id),
      locationPathTruncated: getLocationPathTruncated(sp?.locationDetails?.id)
    }));
  };

  const refreshPaginator = () => {
    spListPaged = spListFILTERED;
    setSpListPagedState(spListPaged);
  };

  const processSpListResponse = (response) => {
    if (response.status) {
      let tempApList = response.data ? response.data : [];
      tempApList = tempApList.map((item) => ({
        ...item,
        displayName: item.name,
        locationId: item.locationDetails ? item.locationDetails.id : "",
        locationName: item.locationDetails ? item.locationDetails.name : ""
      }));
      if (tempApList && tempApList.length > 0) {
        setShowTable(true);
      }
      spList = tempApList;
      setSpListState(tempApList);
    } else {
      spList = [];
      setSpListState([]);
    }
    spListFILTERED = spList.map((sp) => ({
      ...sp,
      locationPath: getLocationPath(sp?.locationDetails?.id),
      locationPathTruncated: getLocationPathTruncated(sp?.locationDetails?.id)
    }));
  };

  const initSpList = (currentPageNo = 1) => {
    setLoading(true);
    BusinessHierarchyService.getSpDevicesList(locationNode.id)
      .then((response) => {
        setLoading(false);
        processSpListResponse(response);
        if (currentPageNo == 1) {
          refreshPaginator();
        } else {
          setSpListPagedState(spListFILTERED);
        }
      })
      .catch((error) => {
        setLoading(false);
        spList = [];
        setSpListState([]);
        spListFILTERED = [];
        refreshPaginator();
        console.log(`Location SP Devices: Error on getspList${error}`);
      });
  };

  const getSpList = (currentPageNo = 1) => {
    setLoading(true);
    BusinessHierarchyService.getSpDevicesList(locationNode.id)
      .then((response) => {
        setLoading(false);
        processSpListResponse(response);
        if (currentPageNo == 1) {
          refreshPaginator();
        } else {
          setSpListPagedState(spListFILTERED);
        }
      })
      .catch((error) => {
        setLoading(false);
        spList = [];
        setSpListState([]);
        spListFILTERED = [];
        refreshPaginator();
        console.log(`Location SP Devices: Error on getSpList${error}`);
      });
  };

  const filterValue = (event) => {
    setSearchText(event.target.value);
    rsearchText.current = event.target.value;
    setShowTable(true);
    if (rsearchText.current.trim().length === 0) {
      getSpList();
    }
  };

  const clearSearch = () => {
    setSearchText("");
    rsearchText.current = "";
    rtotalNoOfItems.current = spCount;
    setShowTable(true);
    getSpList();
  };

  const onAdminSearchFilter = (data) => {
    console.log(data);
    if (rsearchText.current.length > 0) {
      setShowTable(true);
      rtotalNoOfItems.current = data.length;
      searchSpList(data);
      refreshPaginator();
    } else {
      setSearchText("");
      rsearchText.current = "";
      rtotalNoOfItems.current = spCount;
      setShowTable(true);
      getSpList();
    }
  };

  const navigateToSpConfigure = () => {
    navigate("/setup/wirelessnetwork");
  };

  useEffect(() => {
    console.log("LocationSp Mount");
    console.log(`${props} : ${copyIcon}`);
    initSpList();

    return () => {
      console.log("LocationSp Destroy");
    };
  }, []);

  return (
    <div className="ap-wrapper boxShadowBlur" id="sp-wrapper">
      <Flex direction="vertical" gap={16}>
        <Flex align="center" justify="space-between" gap={8} />
        {showTable && spListPagedState && spListPagedState.length >= 0 && (
          <Flex align="center" justify="space-between">
            <Flex justify="space-between">
              <Flex direction="vertical">
                <Search
                  label=""
                  placeholder={i18n.t("proximity-rule.search-text")}
                  options={[]}
                  onChange={filterValue}
                  value={searchText}
                  suffix={searchText.length > 0 && <XCircle title="clear search" size={18} onClick={clearSearch} />}
                />
                {searchText && searchText.length > 0 && (
                  <CustomSearch
                    searchSkeleton={appUserSearchSkeleton}
                    customSearchText={searchText.toLowerCase()}
                    shouldUseGlobalSearch={false}
                    rawData={spListState}
                    onAdminSearchFilter={onAdminSearchFilter}
                  />
                )}
              </Flex>
            </Flex>
          </Flex>
        )}
        {showTable && spListPagedState && spListPagedState.length >= 0 && (
          <div className="userTableContainer">
            <MTable.Pagination key={spListPagedState.length} currentPage={1} defaultRecordsOption="10" showNavigation="all" showTotal showOptions>
              <MTable
                columns={[
                  {
                    accessorKey: "name",
                    header: i18n.t("security-appliance.security-appliance-name"),
                    meta: {
                      align: "left"
                    },
                    enableSorting: "name"
                  },
                  {
                    accessorKey: "macAddress",
                    header: i18n.t("business-location-details.mac-address-text"),
                    meta: {
                      align: "left"
                    },
                    enableSorting: "macAddress"
                  },
                  {
                    accessorKey: "locationPathTruncated",
                    cell: getCellValueWithToolTip,
                    header: i18n.t("location-access-points.location-text"),
                    meta: {
                      align: "left"
                    }
                  }
                ]}
                data={spListPagedState}
              />
            </MTable.Pagination>
          </div>
        )}
        {!showTable && (!rsearchText.current || rsearchText.current.length == 0) && spListPagedState.length == 0 && (
          <div className="team-empty-business">
            <div className="divTable empty_wrapper">
              <div className="divTableRow">
                <div className="divTableCell width50_middle">
                  <div className="title">
                    <Text size="p1" weight="bold">
                      {i18n.t("security-appliance.no-security-appliance-text")}
                    </Text>
                  </div>
                  <div className="desc">
                    <Text size="p2" weight="regular" color="light">
                      {i18n.t("security-appliance.configure-security-appliance-text")}
                    </Text>
                  </div>
                  <Button type="primary" onClick={navigateToSpConfigure}>
                    {i18n.t("security-appliance.configure-security-appliance")}
                  </Button>
                </div>
                <div className="divTableCell width50_middle">
                  <div>
                    <img alt="Security Appliance missing" className="team-empty-img" src={CameraEmpty} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Flex>
    </div>
  );
}
export default SecurityAppliance;
